import {Alert, Button, Stack, Typography} from "@mui/joy"
import {useSessionCookie} from "../hooks/useSessionCookie";
import React from "react"
import {MailWarning} from "lucide-react"
import {useTranslation} from "react-i18next"
import {User} from "../models/user"
import {useAsync} from "../hooks/useAsyncResult"
import {requestEmailVerification as _requestEmailVerification} from "../services/api/user"

/**
 * Prompts accounts that have not verified their email address.
 *
 * Every account created before verification existed reads as unverified, so
 * this is a persistent nudge rather than a block — the API is not gated on
 * `emailVerified` (see `emailVerificationMiddleware` in the backend, which is
 * written but deliberately not mounted).
 */
const EmailVerificationBanner = () => {
    const {t} = useTranslation()
    const {userSession} = useSessionCookie()
    const user: User | undefined = userSession?.['currentUser']
    const {execute, loading, isSuccess} = useAsync(_requestEmailVerification, true, true, false)

    if (!user || user.emailVerified)
        return null

    return <Alert
        variant="soft"
        color={isSuccess ? "success" : "warning"}
        startDecorator={<MailWarning style={{fontSize:30}}/>}
        sx={{marginX: 2, marginBottom: 1, borderRadius: "sm"}}>
        <Stack direction={{xs: "column", sm: "row"}}
               alignItems={{xs: "flex-start", sm: "center"}}
               justifyContent="space-between"
               gap={1}
               width="100%">
            <Stack>
                <Typography level="title-lg">
                    {isSuccess ? t("EMAIL_VERIFICATION_SENT") : t("verifyEmailBannerTitle")}
                </Typography>
                {!isSuccess &&
                    <Typography level="body-sm">
                        {t("verifyEmailBannerDescription", {email: user.email})}
                    </Typography>}
            </Stack>

            {!isSuccess &&
                <Button
                    size="sm"
                    variant="solid"
                    color="warning"
                    loading={loading}
                    onClick={() => execute()}
                    sx={{whiteSpace: "nowrap"}}>
                    {t("resendVerificationEmailActionLabel")}
                </Button>}
        </Stack>
    </Alert>
}

export default EmailVerificationBanner
